import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Users, ArrowLeft, Share2, Loader2, ImageOff } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import { toast } from "sonner";

const TIERS = [25, 50, 100, 250];

/**
 * /fundraise/:slug — public P2P fundraiser page.
 * Donations made here are routed to the foundation's books with fundraiser_id
 * attached so the organizer's page shows the credit.
 */
const FundraisePage = () => {
    const { slug } = useParams();
    const [fr, setFr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState(50);
    const [customAmount, setCustomAmount] = useState("");
    const [form, setForm] = useState({
        donor_name: "",
        donor_email: "",
        message: "",
        anonymous: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        api.get(`/fundraisers/public/${slug}`)
            .then((r) => setFr(r.data))
            .catch(() => setFr(null))
            .finally(() => setLoading(false));
    }, [slug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalAmount = customAmount ? parseFloat(customAmount) : amount;
        if (!finalAmount || finalAmount <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/donations", {
                amount: finalAmount,
                frequency: "one_time",
                fundraiser_id: fr.id,
                ...form,
            });
            setSuccess(data);
            toast.success(`Thanks! Credited to ${fr.organizer_name}'s fundraiser.`);
            // Refresh totals
            const r = await api.get(`/fundraisers/public/${slug}`);
            setFr(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Donation failed");
        } finally {
            setSubmitting(false);
        }
    };

    const share = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({ title: fr.title, url });
            } catch { /* user cancelled */ }
        } else {
            navigator.clipboard.writeText(url);
            toast.success("Link copied — paste it anywhere!");
        }
    };

    if (loading) {
        return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#4A5568]">Loading…</div>;
    }
    if (!fr) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="fundraise-not-found">
                <ImageOff className="w-12 h-12 mx-auto text-[#4A5568] mb-4" strokeWidth={1.5} />
                <p className="font-heading text-2xl text-[#1C252E] mb-2">Fundraiser not found</p>
                <p className="text-[#4A5568] mb-6">It may have been closed by the organizer. Browse other ways to give:</p>
                <Link to="/donate" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-3 text-sm font-medium">
                    Donate to the foundation directly
                </Link>
            </div>
        );
    }

    const raised = fr.raised || 0;
    const goal = fr.goal_amount || 0;
    const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

    if (success) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="fundraise-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F2D8D5] mb-6">
                    <Heart className="w-8 h-8 text-[#E53935]" fill="#E53935" strokeWidth={0} />
                </div>
                <h2 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">Thank you!</h2>
                <p className="text-[#4A5568] mb-8">
                    Your ${parseFloat(success.amount).toFixed(2)} gift goes to <strong>{fr.organizer_name}</strong>'s fundraiser
                    for <strong>{fr.campaign_title}</strong>. Receipt sent to your email.
                </p>
                <button
                    onClick={share}
                    data-testid="fundraise-success-share-btn"
                    className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-6 py-3 text-sm font-medium"
                >
                    <Share2 className="w-4 h-4" strokeWidth={1.5} />
                    Share this fundraiser
                </button>
            </div>
        );
    }

    return (
        <div className="bg-[#FDFBF7] min-h-[calc(100vh-160px)]" data-testid="fundraise-page">
            <SEO
                title={fr.title}
                description={fr.story?.slice(0, 160)}
                path={`/fundraise/${slug}`}
                image={fr.photo_url}
            />

            <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16">
                <Link to="/donate" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#E53935] mb-6" data-testid="fundraise-back-link">
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                    Donate to the foundation directly instead
                </Link>

                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Story */}
                    <div className="lg:col-span-3">
                        {fr.photo_url ? (
                            <img src={fr.photo_url} alt="" className="w-full aspect-video object-cover rounded-3xl bg-[#F4F1EA] mb-6" data-testid="fundraise-photo" />
                        ) : null}
                        <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-3">
                            P2P fundraiser · {fr.campaign_title}
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight mb-3" data-testid="fundraise-title">
                            {fr.title}
                        </h1>
                        <p className="text-sm text-[#4A5568] mb-6">
                            Organized by <strong className="text-[#1C252E]">{fr.organizer_name}</strong>
                        </p>
                        <div className="text-base text-[#1C252E] leading-relaxed whitespace-pre-wrap mb-6" data-testid="fundraise-story">{fr.story}</div>
                        <button
                            onClick={share}
                            data-testid="fundraise-share-btn"
                            className="inline-flex items-center gap-2 bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935] hover:text-[#E53935] rounded-full px-5 py-2.5 text-sm font-medium"
                        >
                            <Share2 className="w-4 h-4" strokeWidth={1.5} />
                            Share this fundraiser
                        </button>
                    </div>

                    {/* Donate panel */}
                    <div className="lg:col-span-2 lg:sticky lg:top-24 self-start">
                        <div className="bg-white border border-black/5 rounded-3xl p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">
                                {goal > 0 ? "Raised so far" : "Current support"}
                            </p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium" data-testid="fundraise-raised">
                                ${raised.toFixed(2)}{goal > 0 && <span className="text-base text-[#4A5568] font-normal"> / ${goal.toFixed(0)}</span>}
                            </p>
                            {goal > 0 ? (
                                <>
                                    <div className="h-2 bg-black/5 rounded-full overflow-hidden mt-3 mb-2">
                                        <div className="h-full bg-[#E53935] transition-all" style={{ width: `${pct}%` }} data-testid="fundraise-progress" />
                                    </div>
                                    <p className="text-xs text-[#4A5568] flex items-center gap-1 mb-5">
                                        <Users className="w-3 h-3" strokeWidth={2} />
                                        {fr.donor_count || 0} donor{(fr.donor_count || 0) === 1 ? "" : "s"} · {pct}% of goal
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-xs text-[#4A5568] mt-2 mb-2">
                                        Flexible fund — no fixed goal.
                                    </p>
                                    <p className="text-xs text-[#4A5568] flex items-center gap-1 mb-5">
                                        <Users className="w-3 h-3" strokeWidth={2} />
                                        {fr.donor_count || 0} donor{(fr.donor_count || 0) === 1 ? "" : "s"} so far
                                    </p>
                                </>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-3" data-testid="fundraise-donate-form">
                                <div className="grid grid-cols-4 gap-2">
                                    {TIERS.map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => { setAmount(t); setCustomAmount(""); }}
                                            data-testid={`fundraise-tier-${t}-btn`}
                                            aria-pressed={amount === t && !customAmount}
                                            className={`py-2 rounded-lg text-sm font-medium transition-all ${amount === t && !customAmount ? "bg-[#1C252E] text-white" : "bg-[#F4F1EA] text-[#1C252E] hover:bg-[#F2D8D5]"}`}
                                        >
                                            ${t}
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type="number"
                                    min="1"
                                    step="0.01"
                                    placeholder="Custom amount"
                                    aria-label="Custom amount"
                                    value={customAmount}
                                    onChange={(e) => setCustomAmount(e.target.value)}
                                    data-testid="fundraise-custom-amount-input"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                                />
                                <input
                                    required
                                    type="text"
                                    placeholder="Your name *"
                                    autoComplete="name"
                                    value={form.donor_name}
                                    onChange={(e) => setForm({ ...form, donor_name: e.target.value })}
                                    data-testid="fundraise-name-input"
                                    aria-label="Donor name"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                                />
                                <input
                                    required
                                    type="email"
                                    placeholder="Email *"
                                    autoComplete="email"
                                    value={form.donor_email}
                                    onChange={(e) => setForm({ ...form, donor_email: e.target.value })}
                                    data-testid="fundraise-email-input"
                                    aria-label="Donor email"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                                />
                                <textarea
                                    placeholder="Message to the organizer (optional)"
                                    rows={2}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    data-testid="fundraise-message-input"
                                    aria-label="Message"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm resize-none"
                                />
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.anonymous}
                                        onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                                        data-testid="fundraise-anonymous-checkbox"
                                        className="w-4 h-4 accent-[#E53935]"
                                    />
                                    <span className="text-xs text-[#4A5568]">Hide my name publicly</span>
                                </label>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="fundraise-submit-btn"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full py-3 font-medium"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" fill="#fff" strokeWidth={0} />}
                                    Donate ${customAmount || amount}
                                </button>
                                <p className="text-[10px] text-[#4A5568] text-center mt-2">
                                    Funds go to Reddog Community Foundation's <strong>{fr.campaign_title}</strong> campaign. {fr.organizer_name} gets credit on this page.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FundraisePage;
